import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { Package, Edit2, Trash2, ArrowLeft, DollarSign, Tag, Truck, Save, X, Image } from 'lucide-react';

export const MyInventoryPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    price: '',
    description: '',
    category: '',
    shipping_method: '',
    sport: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadListings();
  }, [isAuthenticated]);

  const loadListings = async () => {
    try {
      const response = await api().get('/my-listings');
      // Filter out deleted listings
      const activeListings = response.data.filter(l => l.status !== 'deleted');
      setListings(activeListings);
    } catch (error) {
      console.error('Failed to load listings:', error);
      toast.error('Failed to load your inventory');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (listing) => {
    // Only allow owner to edit
    if (listing.seller_id !== user?.id) {
      toast.error('You can only edit your own listings');
      return;
    }
    
    setSelectedListing(listing);
    setEditForm({
      price: listing.price?.toString() || '',
      description: listing.description || '',
      category: listing.category || listing.sport || 'Sports',
      shipping_method: listing.shipping_method || 'standard',
      sport: listing.sport || 'Baseball'
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedListing(null);
    setEditForm({
      price: '',
      description: '',
      category: '',
      shipping_method: '',
      sport: ''
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    if (!selectedListing) return;
    
    // Validate price
    const price = parseFloat(editForm.price);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        price: price,
        description: editForm.description || null,
        category: editForm.category,
        shipping_method: editForm.shipping_method,
        sport: editForm.sport
      };
      
      // Use PATCH endpoint for partial update
      await api().patch(`/cards/${selectedListing.id}`, updateData);
      
      toast.success('Listing updated successfully!');
      closeEditModal();
      
      // Refresh listings to show updated data
      await loadListings();
    } catch (error) {
      console.error('Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (listing) => {
    // Only allow owner to delete
    if (listing.seller_id !== user?.id) {
      toast.error('You can only delete your own listings');
      return;
    }
    setSelectedListing(listing);
    setDeleteDialogOpen(true);
  };

  const handleDeleteListing = async () => {
    if (!selectedListing) return;
    
    setDeleting(true);
    try {
      await api().delete(`/cards/${selectedListing.id}`);
      
      toast.success('Listing deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedListing(null);
      
      // Remove from local state
      setListings(prev => prev.filter(l => l.id !== selectedListing.id));
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  // Get thumbnail image for a listing
  const getThumbnail = (listing) => {
    if (listing.images && listing.images.length > 0) {
      return listing.images[0];
    }
    return listing.image_url || '/placeholder-card.png';
  };

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="my-inventory-page">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl uppercase text-white" data-testid="inventory-title">
              My Inventory
            </h1>
            <p className="text-zinc-400 mt-1">
              {listings.length} {listings.length === 1 ? 'listing' : 'listings'} • Manage your cards
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/sell')}
            className="btn-primary"
            data-testid="add-new-listing-btn"
          >
            <Package className="w-5 h-5 mr-2" />
            Add New Listing
          </Button>
        </div>

        {/* Listings Grid */}
        {listings.length === 0 ? (
          <div className="stat-card p-12 text-center" data-testid="empty-inventory">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">No listings yet</h2>
            <p className="text-zinc-400 mb-6">Start selling your cards to see them here.</p>
            <Button onClick={() => navigate('/sell')} className="btn-primary">
              Create Your First Listing
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <div 
                key={listing.id} 
                className="stat-card overflow-hidden group"
                data-testid={`listing-card-${listing.id}`}
              >
                {/* Card Thumbnail */}
                <div className="aspect-[3/4] relative bg-zinc-800 overflow-hidden">
                  <img
                    src={getThumbnail(listing)}
                    alt={listing.player_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = '/placeholder-card.png';
                    }}
                  />
                  {/* Status Badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                    listing.status === 'active' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  }`}>
                    {listing.status === 'active' ? 'Active' : 'Sold'}
                  </div>
                </div>
                
                {/* Card Info */}
                <div className="p-4">
                  <h3 className="font-medium text-white truncate" data-testid={`listing-name-${listing.id}`}>
                    {listing.player_name}
                  </h3>
                  <p className="text-sm text-zinc-400 truncate">
                    {listing.year} {listing.set_name}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {listing.grade_company} {listing.grade_value || 'Raw'}
                  </p>
                  
                  {/* Price */}
                  <div className="flex items-center gap-1 mt-3">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    <span 
                      className="font-mono font-bold text-xl text-emerald-400"
                      data-testid={`listing-price-${listing.id}`}
                    >
                      {listing.price?.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Action Buttons - Only show for owner */}
                  {listing.seller_id === user?.id && listing.status !== 'sold' && (
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-600 hover:bg-zinc-700"
                        onClick={() => openEditModal(listing)}
                        data-testid={`edit-listing-btn-${listing.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                        onClick={() => openDeleteDialog(listing)}
                        data-testid={`delete-listing-btn-${listing.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  {listing.status === 'sold' && (
                    <div className="mt-4 py-2 text-center text-sm text-amber-400 bg-amber-500/10 rounded">
                      This item has been sold
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Listing Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Listing</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update your listing details. Changes will be reflected immediately on the marketplace.
            </DialogDescription>
          </DialogHeader>
          
          {selectedListing && (
            <div className="space-y-5 py-4">
              {/* Preview Card Name */}
              <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg">
                <div className="w-12 h-16 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                  <img 
                    src={getThumbnail(selectedListing)} 
                    alt={selectedListing.player_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium text-white">{selectedListing.player_name}</p>
                  <p className="text-sm text-zinc-400">{selectedListing.year} {selectedListing.set_name}</p>
                </div>
              </div>
              
              {/* Price Field */}
              <div>
                <Label className="text-zinc-300 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Price (USD) *
                </Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editForm.price}
                    onChange={(e) => handleEditChange('price', e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-600 pl-7"
                    data-testid="edit-price-input"
                  />
                </div>
                <p className="text-xs text-emerald-400 mt-1">You keep 100% - buyers pay the 3.5% fee</p>
              </div>
              
              {/* Description Field */}
              <div>
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Description
                </Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  placeholder="Add details about the card condition, history, etc."
                  className="bg-zinc-800 border-zinc-600 mt-1.5 h-24"
                  data-testid="edit-description-input"
                />
              </div>
              
              {/* Category / Sport Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Category</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(value) => handleEditChange('category', value)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 mt-1.5" data-testid="edit-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sports">Sports Cards</SelectItem>
                      <SelectItem value="Pokemon">Pokemon</SelectItem>
                      <SelectItem value="TCG">Trading Card Games</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Sport</Label>
                  <Select
                    value={editForm.sport}
                    onValueChange={(value) => handleEditChange('sport', value)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 mt-1.5" data-testid="edit-sport-select">
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Baseball">Baseball</SelectItem>
                      <SelectItem value="Basketball">Basketball</SelectItem>
                      <SelectItem value="Football">Football</SelectItem>
                      <SelectItem value="Hockey">Hockey</SelectItem>
                      <SelectItem value="Soccer">Soccer</SelectItem>
                      <SelectItem value="Pokemon">Pokemon</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Shipping Method */}
              <div>
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Shipping Method
                </Label>
                <Select
                  value={editForm.shipping_method}
                  onValueChange={(value) => handleEditChange('shipping_method', value)}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 mt-1.5" data-testid="edit-shipping-select">
                    <SelectValue placeholder="Select shipping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (USPS First Class) - 3-5 days</SelectItem>
                    <SelectItem value="priority">Priority Mail - 1-3 days</SelectItem>
                    <SelectItem value="express">Express - Next Day</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">
                  Shipping rates are calculated via Shippo at checkout
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={closeEditModal}
              className="border-zinc-600"
              data-testid="cancel-edit-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={saving}
              className="btn-primary"
              data-testid="save-changes-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Listing?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{selectedListing?.player_name}"? 
              This action cannot be undone and the listing will be permanently removed from the marketplace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-zinc-800 border-zinc-600 text-white hover:bg-zinc-700"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteListing}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Listing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyInventoryPage;
